// The demo account: the people, areas and projects of the prototype (design/web/prototype.html) and the App Store
// pictures, with days counted from today so the lists always look the same.

import { congratulationID } from "../../core/birthdays";
import { addDays, at, dayParts } from "../../core/days";
import {
  makeArea, makeProject, makeSection, makeTemplate, makeTodo, type Area, type Birthday, type Favorite, type Project, type Section, type Template,
  type TemplateStep, type Todo,
} from "../../core/records";
import type { Day, RepeatRule } from "../../core/types";

export interface SampleAccount {
  todos: Todo[];
  projects: Project[];
  areas: Area[];
  sections: Section[];
  birthdays: Birthday[];
  favorites: Favorite[];
  templates: Template[];
  /** Shares the demo person is in (records under `shares/{id}`), and the invitations waiting for them. */
  shares: SampleShare[];
  invitations: { id: string; shareID: string; kind: string; title: string; fromName: string }[];
}

export interface SampleShare {
  id: string;
  kind: "list" | "project" | "area";
  title: string;
  ownerUID: string;
  members: Record<string, { name: string; email: string; role: "owner" | "member" }>;
  recordID: string;
  projects: Project[];
  todos: Todo[];
  /** This person's own place for a list or project of someone else (`users/{uid}/placements/{id}`). */
  placement?: { areaID: string; order: number };
}

/** The demo's people: the demo person, and two others they share with. */
export const DEMO_UID = "DEMO";
export const ANNA_UID = "ANNA";
export const JONAS_UID = "JONAS";

/** Stable ids, upper-case UUIDs as the apps write them. */
function id(n: number): string {
  return `00000000-0000-4000-8000-${String(n).padStart(12, "0")}`;
}

export function sampleAccount(today: Day, zone: string, now: Date = new Date()): SampleAccount {
  const day = (offset: number) => addDays(today, offset);
  const ago = (days: number, hour = 10) => at(day(-days), hour, 0, zone);
  const weekly: RepeatRule = { interval: 1, unit: "week", afterCompletion: false };
  const daily: RepeatRule = { interval: 1, unit: "day", afterCompletion: false };
  const monthly: RepeatRule = { interval: 1, unit: "month", afterCompletion: false };

  const work = makeArea({ id: id(1), title: "💼 Work", order: 1 });
  const home = makeArea({ id: id(2), title: "🏡 Home", order: 2 });
  const health = makeArea({ id: id(3), title: "🏃 Health", order: 3 });

  const web = makeProject({ id: id(10), title: "Website relaunch", areaID: work.id, order: 2, notes: "The new site goes live before the conference. Copy first, then pictures." });
  const ideas = makeProject({ id: id(11), title: "Product ideas", areaID: work.id, order: 1, kind: "list" });
  const hire = makeProject({ id: id(12), title: "Hire a designer", areaID: work.id, order: 3, deadline: day(30) });
  const offsite = makeProject({ id: id(13), title: "Plan the offsite", areaID: work.id, order: 4, start: { kind: "on", day: day(21) } });
  const lisbon = makeProject({ id: id(14), title: "Lisbon in spring", areaID: home.id, order: 1 });
  const kitchen = makeProject({ id: id(15), title: "Kitchen renovation", areaID: home.id, order: 2, layout: "board" });
  const birthdaysList = makeProject({ id: id(16), title: "Birthdays", areaID: home.id, order: 3, kind: "birthdays" });
  const reading = makeProject({ id: id(17), title: "Reading list", areaID: home.id, order: 4, kind: "list" });
  const tax = makeProject({ id: id(18), title: "Tax return 2026", areaID: home.id, order: 5 });
  const garden = makeProject({ id: id(19), title: "Garden in spring", areaID: home.id, order: 6, start: { kind: "someday" } });
  const marathon = makeProject({ id: id(20), title: "Half marathon", areaID: health.id, order: 1 });

  const focus = makeSection({ id: id(30), title: "Focus", order: 1 });
  const wrapUp = makeSection({ id: id(31), title: "Wrap up the day", order: 2 });
  const copy = makeSection({ id: id(32), title: "Copy", projectID: web.id, order: 1 });
  const launch = makeSection({ id: id(33), title: "Launch", projectID: web.id, order: 2 });
  const toChoose = makeSection({ id: id(34), title: "To Choose", projectID: kitchen.id, order: 1 });
  const ordered = makeSection({ id: id(35), title: "Ordered", projectID: kitchen.id, order: 2 });

  let order = 0;
  const todo = (values: Partial<Todo> & { title: string }, created = ago(3)): Todo =>
    makeTodo({ createdAt: created, order: ++order, ...values });

  const todos: Todo[] = [
    // Today
    // The id the apps give the to-do to congratulate, so it names its birthday (docs/59, 5).
    todo({ id: congratulationID(id(200), dayParts(today).year), title: "Congratulate Anna", start: { kind: "on", day: today }, projectID: birthdaysList.id }),
    todo({ id: id(101), title: "Pay the electricity bill", start: { kind: "on", day: today } }),
    todo({ id: id(102), title: "Sort the inbox", start: { kind: "on", day: today }, repeats: daily, tags: ["Errand"] }),
    todo({ id: id(103), title: "Back up the laptop", start: { kind: "on", day: day(-2) }, deadline: day(-3), repeats: weekly, tags: ["Errand"] }),
    todo({ id: id(104), title: "Approve the payroll run", start: { kind: "on", day: today }, deadline: today, areaID: work.id, repeats: monthly, tags: ["Admin"] }),
    todo({ id: id(105), title: "Send offer to the top candidate", start: { kind: "on", day: today }, deadline: day(2), projectID: hire.id }),
    todo({ id: id(106), title: "Book the dentist appointment", start: { kind: "on", day: today }, areaID: health.id, tags: ["Calls"] }),
    todo({ id: id(107), title: "Plan the long run for Sunday", start: { kind: "on", day: today }, projectID: marathon.id }),
    todo({
      id: id(108), title: "Review the homepage copy", start: { kind: "on", day: today }, projectID: web.id, todaySectionID: focus.id, tags: ["Writing"],
      notes: "Hero and pricing page first. **Keep the tagline.**",
      checklist: [{ id: id(900), title: "Hero", isDone: true }, { id: id(901), title: "Features", isDone: false }, { id: id(902), title: "Pricing page", isDone: false }],
    }),
    todo({
      id: id(109), title: "Water the plants", start: { kind: "on", day: today }, areaID: home.id, todaySectionID: wrapUp.id, repeats: weekly, tags: ["Home"],
      reminder: at(today, 18, 30, zone), seriesReminder: at(today, 18, 30, zone),
    }),
    todo({ id: id(110), title: "Read two chapters", start: { kind: "on", day: today }, projectID: reading.id, todaySectionID: wrapUp.id, reminder: at(today, 21, 0, zone) }),
    // Inbox
    todo({ id: id(120), title: "Ask Jonas about the offsite dates" }, ago(1)),
    todo({ id: id(121), title: "Steuerberater anrufen wegen Belegen" }, ago(1)),
    todo({ id: id(122), title: "Look into standing desks", notes: "Ask in the team channel which ones they like." }, ago(2)),
    todo({ id: id(123), title: "Gift idea for Mia: pottery class" }, ago(2)),
    // Scheduled
    todo({ id: id(130), title: "Book flights to Lisbon", start: { kind: "on", day: day(1) }, projectID: lisbon.id }),
    todo({
      id: id(131), title: "Quarterly review prep", start: { kind: "on", day: day(3) }, areaID: work.id,
      checklist: [{ id: id(903), title: "Numbers", isDone: false }, { id: id(904), title: "Slides", isDone: false }],
    }),
    todo({ id: id(132), title: "Car service", start: { kind: "on", day: day(5) }, areaID: home.id, reminder: at(day(5), 8, 0, zone) }),
    todo({ id: id(133), title: "Final copy sign-off", start: { kind: "on", day: day(5) }, deadline: day(6), projectID: web.id }),
    todo({ id: id(134), title: "Renew the car insurance", start: { kind: "on", day: day(24) }, areaID: home.id }),
    // Anytime
    todo({ id: id(140), title: "Collect receipts", start: { kind: "anytime" }, projectID: tax.id }),
    todo({ id: id(141), title: "Write the launch post", start: { kind: "anytime" }, projectID: web.id, sectionID: launch.id }),
    todo({ id: id(142), title: "Compare tile samples", start: { kind: "anytime" }, projectID: kitchen.id, sectionID: toChoose.id }),
    todo({ id: id(143), title: "Interval run, 6 × 800 m", start: { kind: "anytime" }, projectID: marathon.id, repeats: weekly }),
    todo({ id: id(144), title: "Pick the cabinet handles", start: { kind: "anytime" }, projectID: kitchen.id, sectionID: toChoose.id }),
    todo({ id: id(145), title: "Oven and hob", start: { kind: "anytime" }, projectID: kitchen.id, sectionID: ordered.id }),
    todo({ id: id(146), title: "Find a tax adviser", start: { kind: "anytime" }, projectID: tax.id }),
    todo({ id: id(147), title: "Portfolio reviews", start: { kind: "anytime" }, projectID: hire.id }),
    // Someday
    todo({ id: id(150), title: "Learn to make sourdough", start: { kind: "someday" }, areaID: home.id }),
    todo({ id: id(151), title: "A podcast about product design", start: { kind: "someday" }, projectID: ideas.id }),
    todo({ id: id(152), title: "Repaint the hallway", start: { kind: "someday" }, projectID: kitchen.id }),
    // Stale: untouched for weeks
    todo({ id: id(160), title: "Clean out the garage", start: { kind: "anytime" }, areaID: home.id }, ago(64)),
    todo({ id: id(161), title: "Update the CV", start: { kind: "anytime" }, areaID: work.id }, ago(48)),
    todo({ id: id(162), title: "Sell the old bike", start: { kind: "someday" } }, ago(41)),
    // Done
    todo({ id: id(170), title: "Renew the passport", start: { kind: "on", day: day(-1) }, completedAt: ago(1, 17) }),
    todo({ id: id(171), title: "Draft the job ad", start: { kind: "anytime" }, projectID: hire.id, completedAt: ago(1, 11) }),
    todo({ id: id(172), title: "Easy run, 5 km", start: { kind: "anytime" }, projectID: marathon.id, completedAt: ago(2, 8) }),
    todo({ id: id(173), title: "Long run, 14 km", start: { kind: "anytime" }, projectID: marathon.id, completedAt: ago(4, 9) }),
    todo({ id: id(174), title: "Sitemap", start: { kind: "anytime" }, projectID: web.id, sectionID: copy.id, completedAt: ago(5, 15) }),
    todo({ id: id(175), title: "Book the summer holiday", start: { kind: "anytime" }, completedAt: ago(45, 12) }, ago(60)),
    todo({ id: id(176), title: "File last year's taxes", start: { kind: "anytime" }, completedAt: ago(80, 12) }, ago(90)),
  ];

  const birthYear = dayParts(today).year - 36;
  const { month, date } = dayParts(today);
  const birthdays: Birthday[] = [
    { id: id(200), name: "Anna Berger", month, day: date, year: birthYear, source: "todolix", unknownFields: {} },
    { id: id(201), name: "Jonas Weber", month: dayParts(day(9)).month, day: dayParts(day(9)).date, source: "todolix", unknownFields: {} },
  ];

  // The kit's two examples (TemplateExamples) and one that was used, made one after another.
  const step = (n: number, title: string, more: Partial<TemplateStep> = {}): TemplateStep => ({ id: id(n), title, notes: "", checklist: [], ...more });
  const templates: Template[] = [
    makeTemplate({
      id: id(300), title: "Pack for a Trip", areaID: home.id, order: 1, createdAt: ago(40),
      notes: "Check the weather at the destination the day before, and what the hotel already has.",
      sections: ["Papers", "Clothes", "Bathroom", "Leaving"],
      steps: [
        step(301, "Passport and tickets", { sectionTitle: "Papers" }),
        step(302, "Insurance card and cash", { sectionTitle: "Papers" }),
        step(303, "Clothes for each day, and one more", { sectionTitle: "Clothes" }),
        step(304, "Rain jacket", { sectionTitle: "Clothes" }),
        step(305, "Toothbrush, toiletries, medicine", { sectionTitle: "Bathroom" }),
        step(306, "Chargers and an adapter", { sectionTitle: "Bathroom" }),
        step(307, "Before leaving the house", { sectionTitle: "Leaving", checklist: ["Windows closed", "Plants watered", "Bins out"] }),
      ],
    }),
    makeTemplate({
      id: id(310), title: "Switch to Winter Tyres", areaID: home.id, order: 2, createdAt: ago(30),
      notes: "From October to Easter. Note the tread depth each time; under 4 mm, buy new ones.",
      steps: [
        step(311, "Book the garage", { notes: "Weeks ahead in October: everyone switches at once." }),
        step(312, "Check the tread of the winter set"),
        step(313, "Take the winter set to the garage"),
        step(314, "Store the summer set", { notes: "Lying flat or hung, in a cool, dark place." }),
        step(315, "Check the pressure after 50 km"),
      ],
    }),
    makeTemplate({
      id: id(320), title: "Month-End Close", areaID: work.id, order: 3, createdAt: ago(20), lastUsedAt: ago(11), lastAreaID: work.id, lastWhen: "anytime",
      notes: "Everything to the accountant by the 5th.",
      steps: [step(321, "Collect the receipts"), step(322, "Check the bank statements"), step(323, "Send everything to the accountant")],
    }),
  ];

  // Sharing (docs/43): Lisbon is the demo person's, shared with Anna; Groceries is Anna's, shared with them; Jonas
  // invites them to his book club.
  const people = {
    me: { name: "Bastian", email: "demo@todolix.app" },
    anna: { name: "Anna Berger", email: "anna@example.com" },
    jonas: { name: "Jonas Weber", email: "jonas@example.com" },
  };
  const groceries = makeProject({ id: id(500), title: "Groceries", areaID: home.id, order: 5, kind: "list" });
  const books = makeProject({ id: id(510), title: "Book club", areaID: home.id, order: 1, kind: "list" });
  const lisbonTodos = todos.filter((one) => one.projectID === lisbon.id);
  const shares: SampleShare[] = [
    {
      id: "SHARE-LISBON", kind: "project", title: lisbon.title, ownerUID: DEMO_UID, recordID: lisbon.id,
      members: { [DEMO_UID]: { ...people.me, role: "owner" }, [ANNA_UID]: { ...people.anna, role: "member" } },
      projects: [lisbon], todos: lisbonTodos,
    },
    {
      id: "SHARE-GROCERIES", kind: "list", title: groceries.title, ownerUID: ANNA_UID, recordID: groceries.id,
      members: { [ANNA_UID]: { ...people.anna, role: "owner" }, [DEMO_UID]: { ...people.me, role: "member" } },
      projects: [groceries],
      todos: [
        todo({ id: id(501), title: "Oat milk", start: { kind: "anytime" }, projectID: groceries.id, order: 1 }),
        todo({ id: id(502), title: "Tomatoes", start: { kind: "anytime" }, projectID: groceries.id, order: 2 }),
        todo({ id: id(503), title: "Coffee beans", start: { kind: "anytime" }, projectID: groceries.id, order: 3 }),
        todo({ id: id(504), title: "Bread", start: { kind: "anytime" }, projectID: groceries.id, order: 4, completedAt: ago(1, 18), completedBy: ANNA_UID }),
      ],
      placement: { areaID: home.id, order: 5 },
    },
    {
      id: "SHARE-BOOKS", kind: "list", title: books.title, ownerUID: JONAS_UID, recordID: books.id,
      members: { [JONAS_UID]: { ...people.jonas, role: "owner" } },
      projects: [books],
      todos: [
        todo({ id: id(511), title: "Read chapters 1–5", start: { kind: "anytime" }, projectID: books.id, order: 1 }),
        todo({ id: id(512), title: "Pick the next book", start: { kind: "anytime" }, projectID: books.id, order: 2 }),
      ],
    },
  ];
  const invitations = [{ id: "INVITE-BOOKS", shareID: "SHARE-BOOKS", kind: "list", title: books.title, fromName: "Jonas Weber" }];

  void now;
  return {
    shares,
    invitations,
    templates,
    todos: todos.filter((one) => one.projectID !== lisbon.id),
    projects: [web, ideas, hire, offsite, kitchen, birthdaysList, reading, tax, garden, marathon],
    areas: [work, home, health],
    sections: [focus, wrapUp, copy, launch, toChoose, ordered],
    birthdays,
    favorites: [web.id, tax.id, marathon.id, hire.id, kitchen.id].map((projectID) => ({ kind: "project", id: projectID }) as Favorite),
  };
}
